/**
 * LinkIdentities — Profile → Settings section that lets a signed-in user
 * link additional sign-in methods (Phone / Email link / Google) to their
 * existing account, so future sign-ins via ANY provider map back to the
 * same profile.
 *
 * Backend flow: get a Firebase ID token for the new provider, POST it to
 * `/api/auth/link-identity`. Backend merges any duplicate user record
 * belonging to that identity.
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { firebaseAuth, sendPhoneOtp, sendEmailMagicLink, signInWithGoogle } from "@/lib/firebase";
import { toast } from "sonner";
import { Phone, Mail, Loader2, Check, Link as LinkIcon, ShieldCheck, Sparkles } from "lucide-react";

export default function LinkIdentities() {
  const { user, refresh } = useAuth();
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [emailToLink, setEmailToLink] = useState("");
  const [linkedList, setLinkedList] = useState({ email: null, phone: null, google_uids: 0 });

  useEffect(() => {
    if (!user) return;
    setLinkedList({
      email: user.email || null,
      phone: user.phone_number || null,
      google_uids: (user.firebase_uids || []).length,
    });
  }, [user]);

  const _submitToken = async (idToken) => {
    const { data } = await http.post("/auth/link-identity", { id_token: idToken });
    let msg = "Sign-in method linked.";
    if (data.merged?.length > 0) msg += ` Merged ${data.merged.length} duplicate account(s).`;
    toast.success(msg);
    refresh?.();
  };

  const sendOtp = async () => {
    if (!phone.trim()) return toast.error("Enter your phone in E.164 format, e.g. +15551234567");
    setPhoneBusy(true);
    try {
      const conf = await sendPhoneOtp(phone.trim());
      setConfirmation(conf);
      toast.success("OTP sent — check your SMS.");
    } catch (e) {
      toast.error(e.message || "Couldn't send OTP.");
    } finally { setPhoneBusy(false); }
  };

  const verifyOtp = async () => {
    if (!otp.trim() || !confirmation) return;
    setPhoneBusy(true);
    try {
      const cred = await confirmation.confirm(otp.trim());
      const token = await cred.user.getIdToken();
      await _submitToken(token);
      setPhone(""); setOtp(""); setConfirmation(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Verification failed.");
    } finally { setPhoneBusy(false); }
  };

  const sendEmailLink = async () => {
    if (!emailToLink.trim()) return toast.error("Enter the email you want to link.");
    setEmailBusy(true);
    try {
      await sendEmailMagicLink(emailToLink.trim());
      toast.success("Magic link sent. Open it from any device — you'll return here to finish linking.");
      // NOTE: Because the magic link opens a new tab, the actual link happens on the
      // /login page's email-link handler. It will detect an already-authenticated Social
      // Pulse session and call /auth/link-identity automatically.
      try { window.localStorage.setItem("sp_link_after_email", "1"); } catch { /* ignore */ }
    } catch (e) {
      toast.error(e.message || "Couldn't send the link.");
    } finally { setEmailBusy(false); }
  };

  const linkGoogle = async () => {
    setGoogleBusy(true);
    try {
      const cred = await signInWithGoogle();
      const token = await cred.user.getIdToken();
      await _submitToken(token);
    } catch (e) {
      toast.error(e.message || "Couldn't link Google.");
    } finally { setGoogleBusy(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl space-y-6" data-testid="link-identities">
      <div>
        <h2 className="font-display text-2xl mb-2">Sign-in methods</h2>
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          Add multiple ways to sign in — phone, email, or Google. All of them lead to this same profile.
          If you accidentally created a duplicate account, linking here will merge them automatically.
        </p>
      </div>

      {/* Current identities */}
      <div className="wofy-card !p-4">
        <div className="text-xs font-mono uppercase tracking-widest mb-3 opacity-70">Currently linked</div>
        <div className="space-y-2 text-sm">
          <IdentityRow icon={Mail} label="Email" value={linkedList.email || "— not linked —"} verified={!!user.email_verified_at} />
          <IdentityRow icon={Phone} label="Phone" value={linkedList.phone || "— not linked —"} verified={!!user.phone_verified_at} />
          <IdentityRow icon={ShieldCheck} label="Sign-in methods" value={`${linkedList.google_uids || 1} provider${linkedList.google_uids > 1 ? "s" : ""} (Google, phone, magic link — combined)`} />
        </div>
      </div>

      {/* Link phone */}
      {!linkedList.phone && (
        <div className="wofy-card !p-5">
          <div className="flex items-center gap-2 mb-3">
            <Phone className="w-4 h-4" /> <h3 className="font-semibold">Add a phone number</h3>
          </div>
          {!confirmation ? (
            <div className="flex gap-2 items-center">
              <input className="wofy-input flex-1" placeholder="+15551234567" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="link-phone-input" />
              <button onClick={sendOtp} disabled={phoneBusy} className="wofy-btn-primary text-sm" data-testid="link-send-otp">
                {phoneBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />} Send OTP
              </button>
            </div>
          ) : (
            <div className="flex gap-2 items-center">
              <input className="wofy-input flex-1" placeholder="6-digit code" value={otp} onChange={(e) => setOtp(e.target.value)} data-testid="link-otp-input" />
              <button onClick={verifyOtp} disabled={phoneBusy} className="wofy-btn-primary text-sm" data-testid="link-verify-otp">
                {phoneBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Verify & link
              </button>
            </div>
          )}
          <div id="firebase-recaptcha" />
        </div>
      )}

      {/* Link email */}
      {!linkedList.email && (
        <div className="wofy-card !p-5">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4" /> <h3 className="font-semibold">Add an email</h3>
          </div>
          <div className="flex gap-2 items-center">
            <input className="wofy-input flex-1" type="email" placeholder="you@example.com" value={emailToLink} onChange={(e) => setEmailToLink(e.target.value)} data-testid="link-email-input" />
            <button onClick={sendEmailLink} disabled={emailBusy} className="wofy-btn-primary text-sm" data-testid="link-send-magic">
              {emailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />} Send magic link
            </button>
          </div>
          <p className="text-xs mt-2 opacity-70">
            You'll receive a link — open it and you'll be brought back here with the email linked to this account.
          </p>
        </div>
      )}

      {/* Link Google */}
      <div className="wofy-card !p-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4" /> <h3 className="font-semibold">Link a Google account</h3>
        </div>
        <div className="flex justify-between items-center">
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            Sign in with a Google account you use. If it was previously used to create a separate profile, we'll merge them into this one.
          </p>
          <button onClick={linkGoogle} disabled={googleBusy} className="wofy-btn-primary text-sm shrink-0" data-testid="link-google">
            {googleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Link Google"}
          </button>
        </div>
      </div>
    </div>
  );
}

function IdentityRow({ icon: Icon, label, value, verified }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 opacity-70 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-mono uppercase tracking-widest opacity-60">{label}</div>
        <div className="text-sm truncate">{value}</div>
      </div>
      {verified && <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "#dcfce7", color: "#166534" }}>Verified</span>}
    </div>
  );
}
